import React, { useEffect, useState } from "react";
import { useLang } from "../../context/LangContext";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Users, Plus, Trash2, ShieldCheck, X } from "lucide-react";

const emptyForm = { name: "", phone: "", password: "", email: "" };

export default function AdminAdmins() {
  const { t } = useLang();
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.password) return toast.error(t("fields_required"));
    setSaving(true);
    try {
      await api.post("/admin/admins", {
        name: form.name,
        phone: form.phone,
        password: form.password,
        email: form.email || null,
      });
      toast.success(t("admin_created"));
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm(t("delete_admin_confirm"))) return;
    try {
      await api.delete(`/admin/admins/${id}`);
      toast.success(t("admin_deleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-admins-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
          <Users className="text-[#2A5948]" size={26} />
          {t("manage_admins")}
        </h1>
        <button
          data-testid="add-admin-btn"
          onClick={() => setShowForm(true)}
          className="brand-btn px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 w-fit"
        >
          <Plus size={16} /> {t("add_admin")}
        </button>
      </div>

      <div className="bg-white border border-[#E5E1D8] rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase">
            <tr>
              <th className="text-start px-4 py-3">{t("name")}</th>
              <th className="text-start px-4 py-3">{t("phone")}</th>
              <th className="text-start px-4 py-3">{t("email")}</th>
              <th className="text-start px-4 py-3">{t("joined_at")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-[#5C6053]">—</td></tr>
            )}
            {admins.map((a) => (
              <tr key={a.id} data-testid={`admin-row-${a.id}`} className="border-t border-[#E5E1D8]">
                <td className="px-4 py-3 font-medium flex items-center gap-2">
                  <ShieldCheck size={14} className="text-[#2A5948]" />
                  {a.name}
                  {a.id === user?.id && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#E6F1E7] text-[#2A5948]">You</span>
                  )}
                </td>
                <td className="px-4 py-3" dir="ltr">{a.phone}</td>
                <td className="px-4 py-3">{a.email}</td>
                <td className="px-4 py-3 text-xs text-[#5C6053]">{a.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-end">
                  {a.id !== user?.id && (
                    <button
                      data-testid={`delete-admin-${a.id}`}
                      onClick={() => del(a.id)}
                      className="p-1.5 rounded-lg hover:bg-[#FBE9E4] text-[#C96A52]"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowForm(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl p-6 shadow-2xl border border-[#E5E1D8]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t("add_admin")}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-[#F3EFE7]"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <Field label={t("admin_name")}>
                <input data-testid="admin-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
              </Field>
              <Field label={t("admin_phone")}>
                <input data-testid="admin-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" dir="ltr" placeholder="+9689XXXXXXXX" />
              </Field>
              <Field label={t("admin_password")}>
                <input data-testid="admin-form-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input" minLength={6} />
              </Field>
              <Field label={t("admin_email")}>
                <input data-testid="admin-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" />
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-3 py-2 rounded-lg text-sm border border-[#E5E1D8]">{t("cancel")}</button>
                <button data-testid="admin-form-submit" type="submit" disabled={saving} className="brand-btn px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1 disabled:opacity-60">
                  <Plus size={14} /> {saving ? "..." : t("add_admin")}
                </button>
              </div>
            </form>
            <style>{`.input { width: 100%; border: 1px solid #E5E1D8; background: #F9F6F0; border-radius: 10px; padding: 8px 12px; font-size: 14px; outline: none; }
              .input:focus { box-shadow: 0 0 0 2px rgba(42,89,72,0.25); border-color: #2A5948; }`}</style>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-[#5C6053] mb-1">{label}</div>
      {children}
    </label>
  );
}
